import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import axiosPublic from '../../../../api/axiosPublic';
import { getRootSubdomain } from '../../../utils/subdomain';

const GALLERY_CARD_TITLES =[
  'Primary Years',
  'Middle School',
  'Senior School',
  'Campus Life',
  'Technology',
  'The Arts',
  'Sports',
  'Student Voice',
];

const GALLERY_ACCENTS =['#F39A33', '#00A3E0', '#C5142D', '#FFFFFF', '#F39A33', '#9CA3AF', '#C5142D', '#F39A33'];

// Minimalistic Hover Arrow
const ArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 16 16 12 12 8"></polyline>
    <line x1="8" y1="12" x2="16" y2="12"></line>
  </svg>
);

const CurvedGallerySection = ({
  section,
  sectionKey,
  sectionIndex,
  galleryImages,
  bodyContent,
  renderPositionableElement,
}) => {
  const tenantKey = getRootSubdomain();
  const [syncedGalleryImages, setSyncedGalleryImages] = useState([]);

  useEffect(() => {
    let active = true;

    if (Array.isArray(galleryImages) && galleryImages.length > 0) {
      setSyncedGalleryImages([]);
      return () => {
        active = false;
      };
    }

    if (!tenantKey) {
      setSyncedGalleryImages([]);
      return () => {
        active = false;
      };
    }

    const fetchGalleryImages = async () => {
      try {
        const response = await axiosPublic.get(`/api/${tenantKey}/pages/gallery/all`, {
          skipGlobalLoader: true,
          skipGlobalError: true,
        });
        if (!active) {
          return;
        }

        const fallbackImages = Array.isArray(response?.data)
          ? response.data
              .map((item) => item?.fileUrl || item?.imageUrl || '')
              .filter(Boolean)
          : [];

        setSyncedGalleryImages(fallbackImages);
      } catch (_error) {
        if (active) {
          setSyncedGalleryImages([]);
        }
      }
    };

    fetchGalleryImages();

    return () => {
      active = false;
    };
  }, [galleryImages, tenantKey]);

  const effectiveGalleryImages = useMemo(
    () => (Array.isArray(galleryImages) && galleryImages.length > 0 ? galleryImages : syncedGalleryImages),
    [galleryImages, syncedGalleryImages]
  );

  return (
    <Stack
      spacing={{ xs: 3, md: 4 }}
      sx={{
        mt: 2,
        px: { xs: 2, md: 4 },
        pt: { xs: 4, md: 6 }, // Keep top padding
        pb: { xs: 1, md: 2 }, // FIX 2: Drastically reduced bottom padding to remove dead space
        alignItems: 'center', 
      }}
    >
      {/* ------------------------------------------- */}
      {/* Header Section (Centered & Compact)           */}
      {/* ------------------------------------------- */}
      {(section.title || bodyContent) ? (
        <Stack 
          spacing={1.5} 
          sx={{ 
            color: '#FFFFFF', 
            textAlign: 'center', 
            alignItems: 'center',
            maxWidth: 800, 
          }}
        >
          {section.title ? (
            <Typography
              sx={{
                color: '#F39A33',
                fontWeight: 900,
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                textTransform: 'capitalize',
              }}
            >
              {section.title}
            </Typography>
          ) : null}
          {bodyContent ? (
            <Box sx={{ 
              color: 'rgba(255,255,255,0.75)', 
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              lineHeight: 1.6
            }}>
              {bodyContent}
            </Box>
          ) : null}
        </Stack>
      ) : null}

      {/* ------------------------------------------- */}
      {/* Gallery Grid (Scaled, Centered, Compact)      */}
      {/* ------------------------------------------- */}
      {effectiveGalleryImages.length ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center', 
            gap: { xs: 1.5, md: 2 }, 
            width: '100%',
            maxWidth: 1200, 
          }}
        >
          {effectiveGalleryImages.map((imageUrl, index) => {
            const isIntroCard = index === 0;
            const title = GALLERY_CARD_TITLES[index] || `Gallery ${index + 1}`;
            const accent = GALLERY_ACCENTS[index % GALLERY_ACCENTS.length];

            return (
              <Box
                key={`${sectionKey}-gallery-${index}`}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '6px', // Slightly tighter curve for squares
                  width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 16px)' },
                  minWidth: 220,
                  
                  // FIX 1: Changed from '4/5' to '1/1' (Square). 
                  // This heavily reduces vertical dead space when using objectFit: 'contain'
                  aspectRatio: '1/1', 
                  
                  backgroundColor: '#111111', 
                  
                  // Hover Effects
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  },
                  '&:hover .gallery-img': {
                    transform: 'scale(1.05)', 
                  },
                  '&:hover .gallery-overlay': {
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)',
                  },
                  '&:hover .gallery-arrow': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                  '&:hover .gallery-text-container': {
                    transform: 'translateY(-6px)',
                  }
                }}
              >
                {renderPositionableElement({
                  section,
                  index: sectionIndex,
                  elementKey: `${sectionKey}-gallery-image-${index}`,
                  children: isIntroCard ? (
                    
                    // INTRO CARD
                    <Box
                      sx={{
                        height: '100%',
                        background: 'linear-gradient(135deg, #F39A33 0%, #D35400 100%)',
                        p: { xs: 3, md: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center',     
                        textAlign: 'center',      
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 200,
                          height: 200,
                          background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)',
                          borderRadius: '50%',
                          filter: 'blur(20px)',
                          zIndex: -1,
                        }}
                      />
                      
                      <Stack spacing={1.5} alignItems="center" className="gallery-text-container" sx={{ transition: 'transform 0.4s ease' }}>
                        <Typography
                          sx={{
                            color: '#111111',
                            fontWeight: 900,
                            fontSize: { xs: '1.6rem', md: '1.8rem' },
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {title}
                        </Typography>
                        
                        {section.body ? (
                          <Typography
                            sx={{
                              color: 'rgba(17,17,17,0.85)',
                              fontSize: '0.9rem',
                              lineHeight: 1.5,
                            }}
                          >
                            {String(section.body).replace(/<[^>]+>/g, '').slice(0, 80)}...
                          </Typography>
                        ) : null}
                        
                        <Box className="gallery-arrow" sx={{ opacity: 0.5, transform: 'translateY(10px)', transition: 'all 0.3s ease', color: '#111111', mt: 1 }}>
                          <ArrowIcon />
                        </Box>
                      </Stack>
                    </Box>
                  ) : (

                    // IMAGE CARDS 
                    <Box sx={{ height: '100%', width: '100%', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: -20, 
                          backgroundImage: `url(${imageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          filter: 'blur(24px)', 
                          opacity: 0.5, 
                          zIndex: 0,
                        }}
                      />

                      <Box
                        className="gallery-img"
                        component="img"
                        src={imageUrl}
                        alt={`${section.title || 'Gallery'} ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain', // Kept contain as you requested
                          objectPosition: 'center center', 
                          transition: 'transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)',
                          zIndex: 1, 
                        }}
                      />
                      
                      <Box
                        className="gallery-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(180deg, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.7) 100%)',
                          transition: 'background 0.4s ease',
                          zIndex: 2,
                        }}
                      />
                      
                      <Box
                        className="gallery-text-container"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          p: { xs: 2, md: 2.5 }, // Reduced padding slightly to save space
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center', 
                          textAlign: 'center',
                          transition: 'transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
                          zIndex: 3,
                        }}
                      >
                        <Stack alignItems="center" spacing={0.5}>
                          <Box 
                            sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              backgroundColor: accent,
                              boxShadow: `0 0 10px ${accent}`, 
                            }} 
                          />
                          <Typography
                            sx={{
                              color: '#FFFFFF',
                              fontWeight: 700,
                              fontSize: { xs: '1.05rem', md: '1.15rem' },
                              letterSpacing: '0.02em',
                              textShadow: '0px 2px 8px rgba(0,0,0,0.8)'
                            }}
                          >
                            {title}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  ),
                })}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 6, width: '100%', maxWidth: 600, border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 4 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem' }}>
            Upload gallery images from the admin panel or add custom image URLs in the editor to see this section.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default CurvedGallerySection;
